import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import useSEO from "@/lib/useSEO";
import Reveal from "@/components/Reveal";
import ArrowLink from "@/components/ArrowLink";
import { EASE_HERO } from "@/lib/motion";
import {
  CORE_SERVICES,
  DEVELOPING_SERVICES,
  APPROACH,
  SECTORS,
  ORG_TYPES,
  PEOPLE,
} from "@/data/site";

const HERO_LINES = ["Built on experience.", "Forward in perspective."];

const Hero = () => {
  return (
    <section className="relative pt-28 lg:pt-36 pb-10 lg:pb-16 overflow-hidden" data-testid="home-hero">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              className="flex items-center gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <span className="overline">Chartered Accountants · Est. 1990</span>
              <span className="h-px w-16 bg-brass/60" />
            </motion.div>

            <h1 className="font-serif text-navy text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.04] tracking-tight mt-7">
              {HERO_LINES.map((line, i) => (
                <span className="reveal-line" key={i}>
                  <motion.span
                    initial={{ y: "112%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, ease: EASE_HERO, delay: 0.25 + i * 0.14 }}
                    className={i === 1 ? "italic text-brass" : ""}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              className="mt-8 max-w-xl text-lg sm:text-xl font-light leading-relaxed text-charcoal/85"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: EASE_HERO, delay: 0.62 }}
            >
              Arun Gupta &amp; Co. is a Chartered Accountancy practice established in Kanpur in 1990,
              providing professional support across audit, taxation, accounting, financial reporting
              and evolving business advisory requirements.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE_HERO, delay: 0.78 }}
            >
              <Link to="/services" className="btn-solid" data-testid="hero-explore-btn">
                Explore Our Practice
                <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
              <Link to="/contact" className="btn-line" data-testid="hero-contact-btn">
                Contact Us
                <ArrowUpRight size={15} strokeWidth={1.5} />
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              className="relative overflow-hidden"
              style={{ aspectRatio: "4 / 5" }}
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.15, ease: EASE_HERO, delay: 0.45 }}
            >
              <motion.img
                src="/brand/intro.png"
                alt="Arun Gupta & Co. brand plaque at the firm's Kanpur office — established 1990"
                className="img-cover"
                loading="eager"
              />
              <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 0 1px rgba(23,38,61,0.08)" }} />
            </motion.div>
            <motion.div
              className="mt-4 flex items-center justify-between"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
              <span className="text-xs tracking-wide text-charcoal/55">Kanpur, Uttar Pradesh</span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => {
  const items = [
    "Audit & Assurance",
    "Taxation",
    "GST",
    "Financial Reporting",
    "Business Advisory",
    "Outsourced Finance",
    "Strategic Finance",
  ];
  const loop = [...items, ...items];
  return (
    <section className="py-10 border-y border-border-subtle overflow-hidden" style={{ background: "#EFEBE1" }} aria-hidden="true">
      <div className="marquee">
        {loop.map((t, i) => (
          <span key={i} className="flex items-center whitespace-nowrap">
            <span className="font-serif text-navy/70 text-2xl lg:text-3xl px-8">{t}</span>
            <span className="text-brass text-xl">·</span>
          </span>
        ))}
      </div>
    </section>
  );
};

const Introduction = () => (
  <section className="py-14 lg:py-24" data-testid="home-introduction">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="overline">Introduction</div>
            <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight mt-5">
              Professional experience. Practical perspective.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <p className="text-lg sm:text-xl font-light leading-relaxed text-charcoal/85">
              Over the years, Arun Gupta &amp; Co. has developed its practice around a simple
              principle: professional advice should be grounded in a clear understanding of the
              numbers, the business and the circumstances behind them.
            </p>
            <p className="mt-6 text-base leading-relaxed text-charcoal/75">
              From audit and taxation to financial reporting and business advisory, our approach
              combines technical discipline with practical understanding.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Practice = () => (
  <section className="py-14 lg:py-20 border-t border-border-subtle" data-testid="home-practice">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="overline">Our Practice</div>
            <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 max-w-xl leading-[1.12]">
              Four areas, one standard of care.
            </h2>
          </div>
          <ArrowLink to="/services" testId="practice-all-services-link">All Services</ArrowLink>
        </div>
      </Reveal>

      <div className="mt-14 lg:mt-20">
        {CORE_SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.05}>
            <div className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 py-10 lg:py-12 border-t border-border-subtle">
              <div className="lg:col-span-1">
                <span className="font-serif text-brass text-3xl lg:text-4xl">{s.index}</span>
              </div>
              <div className="lg:col-span-4">
                <h3 className="font-serif text-navy text-2xl lg:text-3xl leading-snug">{s.title}</h3>
              </div>
              <div className="lg:col-span-5">
                <p className="text-base leading-relaxed text-charcoal/80">{s.lead}</p>
              </div>
              <div className="lg:col-span-2 flex lg:justify-end items-start">
                <Link
                  to={`/services#${s.id}`}
                  className="arrow-link"
                  data-testid={`practice-link-${s.id}`}
                >
                  <span className="link-underline">Details</span>
                  <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
        <div className="border-t border-border-subtle" />
      </div>
    </div>
  </section>
);

const Expanding = () => (
  <section className="py-14 lg:py-20 relative overflow-hidden" style={{ background: "#17263D", color: "#F7F4EE" }} data-testid="home-expanding">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="overline">Expanding Our Practice</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight mt-6" style={{ color: "#F7F4EE" }}>
              Expanding the practice.
            </h2>
            <p className="mt-7 text-lg font-light leading-relaxed text-ivory/75 max-w-md">
              As financial requirements become more interconnected, our practice is gradually
              expanding beyond traditional compliance into broader finance and advisory support.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          {DEVELOPING_SERVICES.map((d, i) => (
            <Reveal key={d.id} delay={i * 0.08}>
              <div className="py-9 border-t" style={{ borderColor: "rgba(247,244,238,0.16)" }}>
                <div className="flex items-baseline gap-5">
                  <span className="font-serif text-brass text-2xl">{d.index}</span>
                  <h3 className="font-serif text-ivory text-2xl lg:text-3xl">{d.title}</h3>
                </div>
                <p className="mt-4 text-base leading-relaxed text-ivory/70 lg:pl-12">{d.lead}</p>
              </div>
            </Reveal>
          ))}
          <div className="border-t" style={{ borderColor: "rgba(247,244,238,0.16)" }} />
        </div>
      </div>
    </div>
  </section>
);

const Approach = () => (
  <section className="py-14 lg:py-24" data-testid="home-approach">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <Reveal>
        <div className="overline">Our Approach</div>
        <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 max-w-xl leading-[1.12]">
          Understand first. Advise practically.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-14 lg:mt-20 border-t border-border-subtle">
        {APPROACH.map((a, i) => (
          <Reveal key={a.index} delay={i * 0.08}>
            <div className="pt-10 pb-2 md:pr-10 h-full">
              <span className="font-serif text-brass text-2xl">{a.index}</span>
              <h3 className="font-serif text-navy text-2xl lg:text-3xl mt-4">{a.title}</h3>
              <p className="mt-4 text-base leading-relaxed text-charcoal/75">{a.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Heritage = () => {
  const stages = [
    { year: "1990", label: "Foundation" },
    { year: "Today", label: "Established Practice" },
    { year: "Forward", label: "Evolving Perspective" },
  ];
  return (
    <section className="py-14 lg:py-20 border-t border-border-subtle" style={{ background: "#EFEBE1" }} data-testid="home-heritage">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <Reveal className="relative overflow-hidden" style={{ aspectRatio: "4 / 5" }}>
              <img
                src="/brand/arun-gupta.webp"
                alt="CA Arun Kumar Gupta, founder of Arun Gupta & Co."
                className="img-cover"
                style={{ objectPosition: "center 18%" }}
                loading="lazy"
              />
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <div className="overline">Heritage</div>
              <div className="flex items-center gap-6 mt-5">
                <span className="font-serif text-navy text-7xl lg:text-8xl leading-none">1990</span>
                <span className="h-px flex-1 bg-brass/50" />
              </div>
              <h2 className="font-serif text-navy text-3xl lg:text-4xl tracking-tight mt-6 leading-[1.14]">
                A practice built over time.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-charcoal/80 max-w-xl">
                CA Arun Kumar Gupta qualified as a Chartered Accountant in January 1990 and commenced
                professional practice shortly thereafter. In February 1990, he established Arun Gupta
                &amp; Co., laying the foundation for a professional practice that has continued to
                evolve over the decades.
              </p>
            </Reveal>

            <div className="grid grid-cols-3 gap-4 mt-10">
              {stages.map((s, i) => (
                <Reveal key={s.year} delay={i * 0.08}>
                  <div className="border-t border-navy/20 pt-4">
                    <div className="font-serif text-navy text-xl lg:text-2xl">{s.year}</div>
                    <div className="text-xs tracking-wide text-charcoal/60 mt-1.5">{s.label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Industries = () => (
  <section className="py-14 lg:py-24" data-testid="home-industries">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="overline">Industries</div>
            <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-[2.7rem] tracking-tight mt-5 leading-[1.12]">
              Understanding the business behind the numbers.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-charcoal/75 max-w-sm">
              Our experience extends across businesses with different operating models, commercial
              realities and financial requirements.
            </p>
            <div className="mt-8">
              <ArrowLink to="/industries" testId="home-industries-cta">Explore Our Industries</ArrowLink>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <div className="grid grid-cols-1 sm:grid-cols-2">
            {SECTORS.map((sector, i) => (
              <Reveal key={sector} delay={(i % 2) * 0.05}>
                <div className="py-4 border-b border-border-subtle flex items-baseline gap-4">
                  <span className="text-brass text-xs font-semibold tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-navy text-xl lg:text-2xl">{sector}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {ORG_TYPES.map((o) => (
                <span key={o} className="text-xs tracking-wide text-charcoal/60 border border-border-subtle/50 px-3.5 py-2 bg-white/25">
                  {o}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Forward = () => (
  <section className="py-14 lg:py-20 border-t border-border-subtle" data-testid="home-forward">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <Reveal className="relative overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
            <img src="/brand/office1.png" alt="Reference library of tax and accounting texts at the Arun Gupta & Co. office in Kanpur" className="img-cover" loading="lazy" />
          </Reveal>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <Reveal>
            <div className="overline">Looking Forward</div>
            <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-[1.1] max-w-xl">
              Finance requirements are evolving. So is our practice.
            </h2>
            <p className="mt-7 text-lg font-light leading-relaxed text-charcoal/85 max-w-xl">
              We are building on our established strengths while gradually expanding into financial
              analysis, management information, outsourced finance, advisory and strategic finance
              support.
            </p>
            <p className="mt-5 text-base leading-relaxed text-charcoal/75 max-w-xl">
              This evolution reflects the perspective of the firm's next generation — extending a
              long-standing professional foundation rather than departing from it.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const PeoplePreview = () => (
  <section className="py-14 lg:py-24 border-t border-border-subtle" data-testid="home-people">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="overline">Our People</div>
            <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 max-w-xl leading-[1.12]">
              Experience and perspective behind the practice.
            </h2>
          </div>
          <ArrowLink to="/our-people" testId="home-people-cta">Meet the Team</ArrowLink>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 mt-16 lg:mt-24">
        {PEOPLE.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <Link to="/our-people" className="group block" data-testid={`home-person-${p.id}`}>
              <div className="relative overflow-hidden" style={{ aspectRatio: "4 / 5", background: "#E7E2D8" }}>
                <img
                  src={p.image}
                  alt={`${p.name}, ${p.role}`}
                  className="img-cover"
                  style={{ objectPosition: p.objectPosition }}
                  loading="lazy"
                />
              </div>
              <div className="mt-6 flex items-baseline justify-between">
                <div>
                  <h3 className="font-serif text-navy text-2xl lg:text-3xl">{p.name}</h3>
                  <div className="overline mt-2">{p.role}</div>
                </div>
                <ArrowUpRight className="text-navy/30 group-hover:text-brass transition-colors duration-500" size={18} strokeWidth={1.4} />
              </div>
              <p className="mt-4 text-base leading-relaxed text-charcoal/75 max-w-md">{p.short}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="py-16 lg:py-24 border-t border-border-subtle" style={{ background: "#EFEBE1" }} data-testid="home-cta">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
      <Reveal>
        <div className="overline">Get in Touch</div>
        <h2 className="font-serif text-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.08] max-w-3xl mx-auto">
          Have a requirement you'd like to discuss?
        </h2>
        <div className="mt-10 flex justify-center">
          <Link to="/contact" className="btn-solid" data-testid="home-cta-btn">
            Contact the Firm
            <ArrowRight size={15} strokeWidth={1.5} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  useSEO({
    title: "Chartered Accountants in Kanpur",
    description:
      "Arun Gupta & Co. is a Chartered Accountancy practice established in Kanpur in 1990, offering audit, taxation, GST, accounting, financial reporting and business advisory services.",
    path: "/",
  });
  return (
    <>
      <Hero />
      <Marquee />
      <Introduction />
      <Practice />
      <Expanding />
      <Approach />
      <Heritage />
      <Industries />
      <Forward />
      <PeoplePreview />
      <CTA />
    </>
  );
};

export default Home;
